import React, { useState, useEffect, useCallback } from "react";
import { Modal, ModalBody } from "reactstrap";
import classnames from "classnames";
import axios from "../../../axios-instance";
import Loader from "../../../components/Loader/Loader";
import { notification } from "../../../utils/utility";

const STATUS_TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "declined", label: "Declined" },
  { key: "all", label: "All" },
];

const statusBadgeStyle = (status) => {
  const map = {
    pending: { background: "#FEF3C7", color: "#92400E" },
    approved: { background: "#D1FAE5", color: "#065F46" },
    declined: { background: "#FEE2E2", color: "#991B1B" },
  };
  return {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 9999,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "capitalize",
    ...(map[status] || { background: "#F3F4F6", color: "#374151" }),
  };
};

const personLabel = (person) => {
  if (!person) return "—";
  const name = [person.first_name, person.last_name].filter(Boolean).join(" ");
  return name || person.email || "—";
};

const StaffCalendar = () => {
  const [activeTab, setActiveTab] = useState("pending");
  const [records, setRecords] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(20);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [justification, setJustification] = useState("");

  const loadRequests = useCallback(() => {
    setLoading(true);
    axios
      .post("super-admin/staff-calendar-requests", {
        filter: { status: activeTab },
        pageSize,
        pageNumber,
      })
      .then((res) => {
        setRecords(res?.data?.entity || []);
        setTotalCount(res?.data?.totalCount || 0);
      })
      .catch(() => {
        notification.error("Failed to load staff calendar requests.");
        setRecords([]);
        setTotalCount(0);
      })
      .finally(() => setLoading(false));
  }, [activeTab, pageNumber, pageSize]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const onTabChange = (key) => {
    setActiveTab(key);
    setPageNumber(1);
  };

  const onApprove = (id) => {
    if (!window.confirm("Approve this staff calendar request?")) return;
    setActionLoading(true);
    axios
      .post("super-admin/staff-calendar-requests/approve", { id })
      .then((res) => {
        notification.success(
          res?.data?.message ||
            res?.data?.data?.message ||
            "Request approved."
        );
        loadRequests();
      })
      .catch((err) => {
        notification.error(
          err?.response?.data?.message || "Could not approve request."
        );
      })
      .finally(() => setActionLoading(false));
  };

  const openDecline = (row) => {
    setDeclineTarget(row);
    setJustification("");
  };

  const closeDecline = () => {
    setDeclineTarget(null);
    setJustification("");
  };

  const onDeclineSubmit = (e) => {
    e.preventDefault();
    if (!justification.trim() || justification.trim().length < 3) {
      return notification.error(
        "Please enter a justification (at least 3 characters)."
      );
    }
    setActionLoading(true);
    axios
      .post("super-admin/staff-calendar-requests/decline", {
        id: declineTarget.id,
        justification: justification.trim(),
      })
      .then((res) => {
        notification.success(
          res?.data?.message ||
            res?.data?.data?.message ||
            "Request declined."
        );
        closeDecline();
        loadRequests();
      })
      .catch((err) => {
        notification.error(
          err?.response?.data?.message || "Could not decline request."
        );
      })
      .finally(() => setActionLoading(false));
  };

  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  return (
    <div className="eventdetailsaddbox rd_noshadow">
      <Loader isShowLoader={loading || actionLoading} />
      <div className="boxheader rd_floatingheaderthig">
        <div className="rd_inputselectheader">
          <div className="rd_selectheaderrdt2 rd_selectheaderrdt2profile">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={classnames({ active: activeTab === tab.key })}
                onClick={() => onTabChange(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="box_content p-4">
        <h2 className="mb-3" style={{ fontSize: 20, fontWeight: 700 }}>
          Staff Calendar Requests
        </h2>
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Admin</th>
                <th>Staff</th>
                <th>Quantity</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Dates</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    No requests found.
                  </td>
                </tr>
              )}
              {records.map((row) => (
                <tr key={row.id}>
                  <td>{row.organization?.name || "—"}</td>
                  <td>
                    <div>{personLabel(row.admin)}</div>
                    <small className="text-muted">{row.admin?.email || ""}</small>
                  </td>
                  <td>
                    <div>{personLabel(row.staff)}</div>
                    <small className="text-muted d-block">
                      {row.staff?.email || ""}
                    </small>
                    <small className="text-muted">
                      {row.staff?.phone || ""}
                    </small>
                  </td>
                  <td>{row.quantity}</td>
                  <td style={{ maxWidth: 220, whiteSpace: "pre-wrap" }}>
                    {row.reason || "—"}
                    {row.decline_justification && (
                      <div className="mt-1">
                        <small className="text-danger">
                          Decline: {row.decline_justification}
                        </small>
                      </div>
                    )}
                  </td>
                  <td>
                    <span style={statusBadgeStyle(row.status)}>
                      {row.status}
                    </span>
                  </td>
                  <td>
                    <div>
                      <small>Created: {row.created_at || "—"}</small>
                    </div>
                    <div>
                      <small>Reviewed: {row.reviewed_at || "—"}</small>
                    </div>
                  </td>
                  <td>
                    {row.status === "pending" ? (
                      <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                        <button
                          type="button"
                          className="btn btn-app btn-sm"
                          onClick={() => onApprove(row.id)}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => openDecline(row)}
                        >
                          Decline
                        </button>
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalCount > pageSize && (
          <div className="d-flex align-items-center justify-content-between mt-3">
            <span className="text-muted" style={{ fontSize: 13 }}>
              Page {pageNumber} of {totalPages} ({totalCount} total)
            </span>
            <div>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary mr-2"
                disabled={pageNumber <= 1}
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={pageNumber >= totalPages}
                onClick={() =>
                  setPageNumber((p) => Math.min(totalPages, p + 1))
                }
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal isOpen={!!declineTarget} toggle={closeDecline} centered>
        <ModalBody>
          <div className="rd_modalvieew">
            <div className="box_content rd_modalboxcontent">
              <div className="rd_inputselectheadermodalsetting">
                <button
                  type="button"
                  className="rd_colsebtn"
                  onClick={closeDecline}
                />
              </div>
              <h5 className="rd_modalheadertext">Decline request</h5>
              <p className="rd_modalheadertextparath mt-2 mb-3">
                Provide a justification for declining this staff calendar
                request.
              </p>
              <form onSubmit={onDeclineSubmit}>
                <div className="form-group mb-4">
                  <label className="form-label">Justification</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                    placeholder="Why is this request being declined?"
                    required
                  />
                </div>
                <div className="rd_cancelappbtnmodal">
                  <button
                    type="button"
                    className="rd_cancel"
                    onClick={closeDecline}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-app"
                    disabled={actionLoading}
                  >
                    {actionLoading ? "Declining…" : "Decline"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default StaffCalendar;
